import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import LazyTooltip from 'components/LazyTooltip';
import { RecordLink } from 'components/record-chip/RecordLink';
import { timecardSecondsToHours } from 'pages/timecards/utils/timecardUtils';
import React, { useMemo } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { InvoiceMatterForm, InvoiceTimecard, InvoiceTimecardForm } from 'types/invoice-details';
import { EditIntentTypeCode } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import styles from './TakeActionBackDisplay.module.css';

type Props = {
    editIntentTypeCode: string | null;
    index: number;
    invoiceTimecard: InvoiceTimecard;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    control: Control<InvoiceMatterForm>;
    handleUpdateInvoiceTimecards: (idList: number[], updatedTimecards: InvoiceTimecardForm[]) => void;
};

export const TakeActionBackDisplay = React.memo(
    ({
        index,
        invoiceTimecard,
        editIntentTypeCode,
        setValue,
        getValues,
        control,
        handleUpdateInvoiceTimecards,
    }: Props) => {
        const [newMatter] = useWatch({ control, name: [`invoiceTimecards.${index}.newMatter`] });
        const { isTwoDecimalEntryEnabled } = useTimecardPrecision();
        const { buttonsDisabled } = useSelector((state) => state.buttons);

        const handleToTakeActionBack = () => {
            if (editIntentTypeCode === EditIntentTypeCode.MOVE_TO_ANOTHER_CASE) {
                setValue(`invoiceTimecards.${index}.newMatter`, null, {
                    shouldDirty: true,
                });
                setValue(`invoiceTimecards.${index}.newMatterAssignment`, null, {
                    shouldDirty: true,
                });
            } else if (editIntentTypeCode === EditIntentTypeCode.MARK_AS_NONBILLABLE) {
                setValue(`invoiceTimecards.${index}.isNonBillable`, false, {
                    shouldDirty: true,
                });
                setValue(
                    `invoiceTimecards.${index}.billedTimeInHours`,
                    String(timecardSecondsToHours(invoiceTimecard.billedTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)),
                    { shouldDirty: true }
                );
            }
            setValue(`invoiceTimecards.${index}.editIntentTypeCode`, null, { shouldDirty: true });
            handleUpdateInvoiceTimecards([invoiceTimecard.id], getValues('invoiceTimecards'));
        };

        const invoiceTimecardChangedText = useMemo(() => {
            switch (editIntentTypeCode) {
                case EditIntentTypeCode.REMOVE_VIA_CORRECTION: {
                    return t('invoices.details.table.timecard-is-credited');
                }
                case EditIntentTypeCode.MOVE_TO_ANOTHER_CASE: {
                    return t('invoices.details.table.timecard-is-moved');
                }
                case EditIntentTypeCode.MARK_AS_NONBILLABLE: {
                    return t('invoices.details.table.timecard-is-non-billable');
                }
                case EditIntentTypeCode.POSTPONE: {
                    return t('invoices.details.table.timecard-is-postponed');
                }
                default: {
                    return '';
                }
            }
        }, [editIntentTypeCode]);

        const invoiceTimecardToolTipText = useMemo(() => {
            switch (editIntentTypeCode) {
                case EditIntentTypeCode.REMOVE_VIA_CORRECTION: {
                    return t('invoices.details.table.timecard-is-credited-tooltip');
                }
                case EditIntentTypeCode.MOVE_TO_ANOTHER_CASE: {
                    return t('invoices.details.table.timecard-is-moved-tooltip');
                }
                case EditIntentTypeCode.MARK_AS_NONBILLABLE: {
                    return t('invoices.details.table.timecard-is-non-billable-tooltip');
                }
                case EditIntentTypeCode.POSTPONE: {
                    return t('invoices.details.table.timecard-is-postponed-tooltip');
                }
                default: {
                    return '';
                }
            }
        }, [editIntentTypeCode]);

        return (
            <div className={styles.container}>
                <div>
                    <span className={styles.actionText}>
                        {invoiceTimecardChangedText}
                        <LazyTooltip title={invoiceTimecardToolTipText} placement="bottom">
                            <InfoOutlinedIcon className={cn(styles.infoIcon, 'text-grey-400')} />
                        </LazyTooltip>
                        <button
                            type="button"
                            onClick={handleToTakeActionBack}
                            disabled={buttonsDisabled}
                            className="text-fg hover:text-link cursor-pointer underline disabled:pointer-events-none disabled:opacity-50"
                        >
                            <FormattedMessage id="invoices.actions.take-back" />
                        </button>
                    </span>
                </div>
                <span className={styles.matterText}>
                    {newMatter ? (
                        <RecordLink
                            to={`/matter/${newMatter.id}`}
                            className="text-fg hover:text-link w-fit no-underline hover:underline"
                            label={newMatter.name}
                            withOrigin
                        />
                    ) : null}
                </span>
            </div>
        );
    }
);
